import { BCMSDate, BCMSDefaultModel, BCMSRecord, Link, ModalOpen, useGlobalController, useRoute, useWorkflowStep } from "@bcm-dev/bcms-vite";
import ButtonContainer from 'compid/shared/containers/ButtonContainer';
import PageLayoutContainer from 'compid/shared/containers/PageLayoutContainer';
import CenterColumnContainer from 'compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer';
import PageView from 'compid/shared/containers/PageViewContainer';
import AutopayFormView from 'compid/shared/views/AutopayFormView';
import BillerView from 'compid/shared/views/BillerView';
import InfoAlertNotificationView from 'compid/shared/views/Notifications/InfoNotificationView/components/InfoAlertNotificationView';
import UIButton from 'compid/ui/UIButton';
import { getEditAutopay, getWorkflowSteps } from "compid/workflows/AddAutoPay";
import { Autopay, Bill, Biller, Payment } from "model";
import React from 'react';
import "./styles.css";

/**
 * @function EditAutopayPage
 * @return {React.Component}
 * @exported true
 * @description The page that allows the user to edit the autopay plan
 */

export default function EditAutopayPage(props) {
  const controller = useGlobalController();
  // Get the workflow step and initalize model classes
  const workflowStep = useWorkflowStep(getEditAutopay, getWorkflowSteps);
  BCMSRecord.globalControllerFor([ Autopay, Bill, Biller, Payment ], controller);
  const navigate = useRoute();
  const bill = workflowStep.read("bill");
  const biller = workflowStep.read("biller");
  const autopay = workflowStep.read("autopay");
  const payment = workflowStep.read("payment");

  // "Leave" button is clicked in the "Are you sure you want to leave?" modal.
  const onLeave = () => navigate(-1);

  // "Delete plan" button is clicked in the "Delete autopay plan?" modal.
  const onDeletePlanClick = () => {
    //workflowStep.update(["payment", "status"], "canceled");
    workflowStep.update(["bill", "payment"], payment);
    let paymentRecordsToCancel = [];

    Payment.initAll().map((item) => {
      if(item.autopay) {
        if(item.autopay.id === autopay.id) {
          item.status = "canceled";
          item.autopay = undefined;
          paymentRecordsToCancel.push(Payment.update(item))
        }
      }
    });

    // The associated payment on the biller details page should have a status updated to "Canceled".
    // Cancel all existing payments with this autopay id.
    BCMSRecord.batch([
        Autopay.delete(autopay.id),
        Bill.update(bill),
        Biller.update({ ...biller.record, autopay: undefined }),
        ...paymentRecordsToCancel,
        //Payment.update(payment),
    ], workflowStep.globalController);

    navigate(`/billerDetails/details/${ biller.id }`, { state: { autopayDeleted: "true", autopay: autopay, biller: biller } });
  };

  const onSaveClick = () => {
      // The associated payment on the biller details page should have a status updated to "Canceled".
      // A new payment should be made with the status "scheduled".
      // Cancel all existing payments with this autopay id.
      workflowStep.update(["biller", "autopay"], autopay);
      //workflowStep.update(["payment", "status"], "canceled");
      //workflowStep.update(["payment", "autopay"], undefined);

      const newPayment = BCMSDefaultModel.payment();
      newPayment.autopay = autopay; // use autopay as a class instead of id
      newPayment.id = Payment.nextID();
      newPayment.to = biller;
      newPayment.hasRelatedAutopay = true;

      let amount;
      // Check which amount should be used
      if (autopay.alwaysPay === "Amount due") { amount = bill.amount; }
      else if (autopay.alwaysPay === "Fixed amount") { amount = autopay.fixed; }
      else { amount = autopay.alwaysPay; }

      newPayment.amount = amount;

      workflowStep.update(["bill", "payment"], newPayment);

      let paymentRecordsToCancel = [];

      Payment.initAll().map((item) => {
        if(item.autopay) {
          if(item.autopay.id === autopay.id) {
            item.status = "canceled";
            paymentRecordsToCancel.push(Payment.update(item))
          }
        }
      });

      const defaultAutopay = BCMSDefaultModel.autopay();
      const updatedAutopay = Autopay.init(BCMSDefaultModel.mergeRecords(autopay, defaultAutopay));
      const numberOfPaymentsNumeric = parseFloat(updatedAutopay.numberOfPayments);
      if(numberOfPaymentsNumeric === 0) updatedAutopay.numberOfPayments = defaultAutopay.numberOfPayments;
      if(updatedAutopay.duration !== "Until a specific date") updatedAutopay.finalDeliveryBy = BCMSDate.init().offset(-1);

      // Update the global model
      BCMSRecord.batch([
          Autopay.update(updatedAutopay),
          Biller.update(biller),
          Bill.update(bill),
          Payment.create(newPayment),
          ...paymentRecordsToCancel,
          //Payment.update(payment),
      ], workflowStep.globalController);

      workflowStep.workflow.nextStep(workflowStep.getStepState);
  };

  return(
      <PageView className="edit-autopay-page">
        <p className="page-sub-header semi-bold-text">EDIT AUTOPAY</p>

        <PageLayoutContainer>
          <div className="flex-container flex-vertical flex-width">
            <div className="flex-height flex-container flex-vertical upper-content-container">
              <InfoAlertNotificationView text="Changes will apply to all future payments scheduled from this autopay plan." />
              <BillerView biller={ biller } />
            </div>

            <div className="flex-height flex-width flex-container all-center">
              <CenterColumnContainer>
                <AutopayFormView isEditing={ true }>
                  <ButtonContainer className="form-button-container button-container flex-container flex-horizontal edit-autopay-button-container">
                      <UIButton className="secondary-button">
                        <ModalOpen id="WantToLeaveModal" data={{ onLeave: onLeave }}>Don't save changes</ModalOpen>
                      </UIButton>
                      <UIButton className="primary-button" onClick={ onSaveClick }>Save changes</UIButton>
                  </ButtonContainer>
                  <Link className="text-center semi-bold-text delete-autopay-link link-button" >
                    <ModalOpen id="DeleteAutoPayModal" data={{ onDeletePlanClick: onDeletePlanClick }}>Delete autopay</ModalOpen>
                  </Link>
                </AutopayFormView>
              </CenterColumnContainer>
            </div>
          </div>
        </PageLayoutContainer>
      </PageView>
  );
}